/**
 * Lightweight prefetch-on-hover for near-instant in-store navigation.
 *
 * On Shopify every click is a full server-rendered page load. By prefetching a
 * link's HTML as soon as the user shows intent (hover on desktop, touchstart on
 * mobile) the document is already in the browser cache by the time they click,
 * so the navigation feels close to instant.
 *
 * Defensive by design: only same-origin GET document links, never cart/checkout/
 * account/logout or anything that mutates state, and it backs off on slow
 * connections or when the user has Data Saver enabled.
 */
(function () {
  'use strict';

  // Bail on data-saver / slow connections — prefetching there wastes the user's data.
  var conn = navigator.connection;
  if (conn) {
    if (conn.saveData) return;
    if (/(^|-)2g$/.test(conn.effectiveType || '')) return;
  }

  if (!('IntersectionObserver' in window)) return; // very old browser, skip silently

  var origin = window.location.origin;
  var prefetched = new Set();
  var MAX_PREFETCH = 50; // safety cap per page
  var HOVER_DELAY = 65; // ms of sustained intent before prefetching
  var hoverTimer = null;

  // Path fragments that must never be prefetched (state-changing or per-user).
  var BLOCKLIST = /\/(cart|checkout|account|orders|apps|\d+\/orders)(\/|$|\?)|\/logout|add\.js|change\.js|\/wishlist/i;

  function eligible(a) {
    if (!a || a.tagName !== 'A') return false;
    if (a.origin !== origin) return false; // external
    if (a.hasAttribute('download')) return false;
    if (a.target && a.target !== '' && a.target !== '_self') return false;
    if (a.dataset.noPrefetch !== undefined) return false;
    var href = a.href;
    if (!href || href.indexOf('#') === 0) return false;
    // Same page (hash-only navigation)
    if (a.pathname === window.location.pathname && a.search === window.location.search && a.hash) return false;
    if (BLOCKLIST.test(a.pathname + a.search)) return false;
    if (prefetched.has(href)) return false;
    return true;
  }

  function prefetch(href) {
    if (prefetched.size >= MAX_PREFETCH) return;
    prefetched.add(href);
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    document.head.appendChild(link);
  }

  function onEnter(e) {
    var a = e.target.closest && e.target.closest('a');
    if (!eligible(a)) return;
    hoverTimer = setTimeout(function () {
      prefetch(a.href);
    }, HOVER_DELAY);
  }

  function onLeave() {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
  }

  // Desktop intent: pointer hover with a short dwell.
  document.addEventListener('mouseover', onEnter, { passive: true, capture: true });
  document.addEventListener('mouseout', onLeave, { passive: true, capture: true });

  // Mobile intent: the tap starts ~100-200ms before the click fires.
  document.addEventListener(
    'touchstart',
    function (e) {
      var a = e.target.closest && e.target.closest('a');
      if (eligible(a)) prefetch(a.href);
    },
    { passive: true, capture: true }
  );
})();
